import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import api, { messagingService, getWsOrigin } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import { isImageAttachment, attachmentFileName } from '../utils/attachments';

function Messages() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const location = useLocation();
  const [selectedChat, setSelectedChat] = useState(null);
  const [mobileView, setMobileView] = useState('list');
  const [messageInput, setMessageInput] = useState('');
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const convSocketRef = useRef(null);
  const reconnectRef = useRef({ attempts: 0, timeoutId: null });
  const [conversations, setConversations] = useState([]);

  const makeAbsolute = (url) => {
    if (!url) return null;
    try {
      const u = new URL(url);
      return u.href;
    } catch (e) {
      if (url.startsWith('/')) return `${window.location.origin}${url}`;
      return url;
    }
  };

  // The REST response for a sent message and its WebSocket broadcast (added so
  // other participants see it live) can both resolve into this same state —
  // whichever arrives second must be deduped by id instead of appended again.
  const appendMessage = (msg) => {
    setMessages((prev) => (msg?.id != null && prev.some((m) => m.id === msg.id)) ? prev : [...prev, msg]);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Open global user-level WS to receive notifications (updates conversation previews/unread)
  useEffect(() => {
    let ws;
    try {
      const token = localStorage.getItem('accessToken');
      const url = `${getWsOrigin()}/ws/messaging/user/?token=${token}`;
      ws = new WebSocket(url);
      ws.onopen = () => console.log('Messages page user WS connected');
      ws.onmessage = async (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === 'notification') {
            const payload = data.payload || {};
            // notifications may wrap the actual message inside `payload.payload` or `payload.message`
            const msg = payload.payload || payload.message || payload;
            // support multiple naming conventions for ids (chat_id, group_id, conversation_id, etc.)
            const groupId = payload.chat_id || payload.group_id || payload.group || payload.groupChatId || payload.group_chat_id;
            const privateId = payload.conversation_id || payload.private_conversation_id || payload.private_id;
            const convType = groupId ? 'group' : (privateId ? 'private' : null);

            if (convType) {
              setConversations(prev => {
                const next = prev.map(c => {
                  if ((convType === 'group' && c.type === 'group' && String(c.id) === String(groupId)) || (convType === 'private' && c.type === 'private' && String(c.id) === String(privateId))) {
                    const unread = (c.unread_count || 0) + ((selectedChat && c.id === selectedChat.id) ? 0 : 1);
                    return { ...c, last_message: msg, unread_count: unread };
                  }
                  return c;
                });
                // recompute total unread and notify sidebar
                const totalUnread = next.reduce((s, x) => s + (x.unread_count || 0), 0);
                window.dispatchEvent(new CustomEvent('conversations.unread_updated', { detail: { unreadCount: totalUnread } }));
                return next;
              });

              // If the notification is for the currently opened conversation, append message to messages.
              // Guard against duplicates: the conversation-specific socket (below) already
              // delivers this same message to whoever has the chat open, including the sender.
              if ((convType === 'group' && selectedChat?.isGroup && String(selectedChat.id) === String(groupId)) || (convType === 'private' && !selectedChat?.isGroup && String(selectedChat?.id) === String(privateId))) {
                setMessages(prev => (msg?.id != null && prev.some(m => m.id === msg.id)) ? prev : [...prev, msg]);
              }
            } else {
              // Fallback: refresh entire conversations list
              try {
                const resp = await api.get('/messaging/conversations/');
                const data = resp.data || resp;
                const rawList = Array.isArray(data) ? data : data.results || [];
                const list = rawList.map(c => ({ ...c, thumbnail: makeAbsolute(c.thumbnail), avatar: makeAbsolute(c.avatar) || null }));
                const totalUnread = list.reduce((s, c) => s + (c.unread_count || 0), 0);
                setConversations(list);
                window.dispatchEvent(new CustomEvent('conversations.unread_updated', { detail: { unreadCount: totalUnread } }));
              } catch (e) { console.error('Failed to refresh conversations on notification', e); }
            }
          }
        } catch (e) { console.error('User WS parse error', e); }
      };
      ws.onclose = () => console.log('Messages page user WS closed');
      ws.onerror = (e) => console.error('Messages page WS error', e);
    } catch (e) {}

    return () => { try { ws && ws.close(); } catch (e) {} };
  }, [selectedChat]);

  const { t } = useTranslation();
  const { addNotification } = useNotification();

  const formatParticipantRoles = (roles = []) => roles
    .map((role) => ({
      administrator: t('Administrator'),
      teacher: t('Teacher'),
      parent: t('Parent'),
      student: t('Student'),
    }[role] || role))
    .join(' • ');

  useEffect(() => {
    if (selectedChat && selectedChat.messages) {
      setMessages(selectedChat.messages);
    }
  }, [selectedChat]);

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const resp = await api.get('/messaging/conversations/');
        const data = resp.data || resp;
        console.log('Fetched conversations:', data);
        const rawList = Array.isArray(data) ? data : data.results || [];

        const list = rawList.map(c => ({
          ...c,
          thumbnail: makeAbsolute(c.thumbnail),
          avatar: makeAbsolute(c.avatar) || null,
        }));
        setConversations(list);

        // If no chat is selected, open the first group conversation, or first conversation
        if (!selectedChat) {
          // If navigation requested a specific conversation (via state or query param), don't auto-open the first group.
          const req = location?.state?.openConversation;
          const urlParams = new URLSearchParams(window.location.search);
          const targetUser = urlParams.get('target_user');
          if (!req && !targetUser) {
            const firstGroup = list.find(c => c.type === 'group');
            const first = firstGroup || list[0];
            if (first) {
            // load messages for the conversation
              if (first.type === 'group') {
                console.log('Loading messages for group', first.id);
                const msgsRes = await api.get(`/messaging/group-chats/${first.id}/messages/`);
                console.log('Group messages response:', msgsRes);
                const msgs = msgsRes.data || [];
              setSelectedChat({ id: first.id, name: first.title, isGroup: true, avatar: first.thumbnail || null, subtitle: first.subtitle, participantRoles: [], messages: msgs });
              setMessages(msgs);
            } else {
                console.log('Loading messages for private convo', first.id);
                const msgsRes = await api.get(`/messaging/private-conversations/${first.id}/messages/`);
                console.log('Private messages response:', msgsRes);
                const msgs = msgsRes.data || [];
              setSelectedChat({ id: first.id, name: first.title, isGroup: false, avatar: first.avatar, subtitle: first.subtitle, participantRoles: first.participant_roles || [], messages: msgs });
              setMessages(msgs);
            }
            }
          }
        }
        // if user has selectedChat already open, ensure websocket is (re)connected to it
        // no-op here; websocket lifecycle handled by selectedChat effect
      } catch (err) {
        console.error('Failed to load conversations', err);
      }
    };
    fetchConversations();
  }, []);

  // Open websocket for the selected conversation for realtime updates
  useEffect(() => {
    if (!selectedChat) return;

    const token = localStorage.getItem('accessToken');
    const path = selectedChat.isGroup ? `/ws/messaging/group/${selectedChat.id}/` : `/ws/messaging/private/${selectedChat.id}/`;
    const url = `${getWsOrigin()}${path}?token=${token}`;

    let ws;
    let intentionalClose = false;
    const createWs = () => {
      ws = new WebSocket(url);

      ws.onopen = () => {
        console.log('Conversation WS connected', url);
        reconnectRef.current.attempts = 0;
        if (reconnectRef.current.timeoutId) {
          clearTimeout(reconnectRef.current.timeoutId);
          reconnectRef.current.timeoutId = null;
        }
        convSocketRef.current = ws;
      };

      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === 'message') {
            const msg = data.payload;
            setMessages(prev => (msg?.id != null && prev.some(m => m.id === msg.id)) ? prev : [...prev, msg]);
            // update conversations list and unread badge (normalize media URLs)
            (async () => {
              try {
                const convsResp = await api.get('/messaging/conversations/');
                const convsData = convsResp.data || convsResp;
                const convsListRaw = Array.isArray(convsData) ? convsData : convsData.results || [];
                const convsList = convsListRaw.map(c => ({ ...c, thumbnail: makeAbsolute(c.thumbnail), avatar: makeAbsolute(c.avatar) }));
                // update preview for selected chat if present
                const convsListUpdated = convsList.map(c => (c.id === selectedChat?.id && c.type === (selectedChat?.isGroup ? 'group' : 'private') ? { ...c, last_message: msg } : c));
                const totalUnread = convsListUpdated.reduce((s, c) => s + (c.unread_count || 0), 0);
                window.dispatchEvent(new CustomEvent('conversations.unread_updated', { detail: { unreadCount: totalUnread } }));
                setConversations(convsListUpdated);
              } catch (e) { console.error('Failed to refresh conversations after WS message', e); }
            })();
          } else if (data.type === 'read') {
            const payload = data.payload || {};
            const messageIds = payload.message_ids || [];
            setMessages(prev => prev.map(m => (messageIds.includes(m.id) ? { ...m, is_read: true } : m)));
          }
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        } catch (e) { console.error('WS parse error', e); }
      };

      ws.onclose = () => {
        // A close triggered by our own cleanup (switching chats, unmounting) is
        // not a dropped connection — reconnecting would leave a zombie socket
        // still listening on the old conversation, which double-delivers
        // messages whenever a new one is created shortly after.
        if (intentionalClose) return;
        console.log('Conversation WS closed, scheduling reconnect');
        reconnectRef.current.attempts += 1;
        const backoff = Math.min(30000, 1000 * 2 ** (reconnectRef.current.attempts - 1));
        reconnectRef.current.timeoutId = setTimeout(() => createWs(), backoff);
      };

      ws.onerror = (e) => console.error('Conversation WS error', e);
    };

    createWs();

    return () => {
      intentionalClose = true;
      try { ws.close(); } catch (e) {}
      convSocketRef.current = null;
      if (reconnectRef.current.timeoutId) clearTimeout(reconnectRef.current.timeoutId);
      reconnectRef.current.attempts = 0;
    };
  }, [selectedChat]);

  // If navigated here with an `openConversation` request in location.state, ensure it's opened
  useEffect(() => {
    const openRequestedConversation = async () => {
      const req = location.state?.openConversation;
      // If no state request, check for URL query fallback `target_user`
      const urlParams = new URLSearchParams(window.location.search);
      const targetUser = urlParams.get('target_user');
      if (!req && targetUser) {
        try {
          const parsed = parseInt(targetUser, 10);
          if (!isNaN(parsed)) {
            // create a request object from query param
            location.state = { openConversation: { type: 'private', userId: parsed } };
          }
        } catch (e) {}
      }
      if (!req) return;

      try {
        if (req.type === 'private' && req.conversationId) {
          const convoId = req.conversationId;
          // fetch messages for the conversation id directly
          const msgsRes = await api.get(`/messaging/private-conversations/${convoId}/messages/`);
          const msgs = msgsRes.data || [];

          // Fetch the combined overview because it contains the other
          // participant's display name, avatar and roles.
          let convoMeta = null;
          try {
            const all = await api.get('/messaging/conversations/');
            const data = all.data || all;
            const convos = Array.isArray(data) ? data : data.results || [];
            convoMeta = convos.find(c => c.type === 'private' && String(c.id) === String(convoId));
          } catch (e) {}

          const selected = {
            id: convoId,
            name: convoMeta?.title || convoMeta?.display_name || convoMeta?.participants_display || 'Conversation',
            isGroup: false,
            avatar: makeAbsolute(convoMeta?.avatar) || null,
            subtitle: convoMeta?.subtitle || '',
            participantRoles: convoMeta?.participant_roles || [],
            messages: msgs,
          };
          setSelectedChat(selected);
          setMessages(msgs);
          try { window.history.replaceState({}, document.title, window.location.pathname); } catch (e) {}
          return;
        }

        if (req.type === 'private' && req.userId) {
          // Try to find existing private conversation between current user and requested user
          const res = await api.get('/messaging/conversations/');
          const data = res.data || res;
          const convos = Array.isArray(data) ? data : data.results || [];
          let convo = convos.find(c => c.type === 'private' && String(c.other_user_id) === String(req.userId));
          if (!convo) {
            const createRes = await messagingService.createPrivateConversation({ user_id: req.userId });
            const createdId = createRes.data?.id;
            const refreshed = await api.get('/messaging/conversations/');
            const refreshedData = refreshed.data || refreshed;
            const refreshedConversations = Array.isArray(refreshedData) ? refreshedData : refreshedData.results || [];
            convo = refreshedConversations.find(c => c.type === 'private' && String(c.id) === String(createdId));
          }

          if (!convo) throw new Error('Conversation metadata is unavailable');

          // fetch messages for the conversation
          const msgsRes = await api.get(`/messaging/private-conversations/${convo.id}/messages/`);
          const msgs = msgsRes.data || [];

          const selected = {
            id: convo.id,
            name: convo.title || convo.display_name || convo.participants_display || 'Conversation',
            isGroup: false,
            avatar: convo.avatar || null,
            subtitle: convo.subtitle || '',
            participantRoles: convo.participant_roles || [],
            messages: msgs,
          };

          setSelectedChat(selected);
          setMessages(msgs);
          // Clear navigation state to avoid reopening on back/refresh
          try { window.history.replaceState({}, document.title, window.location.pathname); } catch (e) {}
        }
      } catch (err) {
        console.error('Failed to open requested conversation', err);
      }
    };
    openRequestedConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = () => {
    if (!messageInput.trim() || !selectedChat) return;
    const content = messageInput.trim();
    setMessageInput('');

    // Prefer websocket when available for instant delivery, fallback to REST
    try {
      const ws = convSocketRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ content }));
        return;
      }
    } catch (e) { /* continue to REST fallback */ }

    (async () => {
      try {
        if (selectedChat.isGroup) {
          const res = await messagingService.sendGroupMessage(selectedChat.id, { content });
          appendMessage(res.data || {});
        } else {
          const res = await messagingService.sendPrivateMessage(selectedChat.id, { content });
          appendMessage(res.data || {});
        }
      } catch (err) {
        console.error('Failed to send message', err);
        addNotification && addNotification?.('Failed to send message', 'error');
      }
    })();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleAttachmentChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !selectedChat) return;

    if (file.size > 25 * 1024 * 1024) {
      addNotification?.('Attachment must be smaller than 25MB', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('attachment', file);

    try {
      const res = selectedChat.isGroup
        ? await messagingService.sendGroupMessage(selectedChat.id, formData)
        : await messagingService.sendPrivateMessage(selectedChat.id, formData);
      appendMessage(res.data || {});
    } catch (err) {
      addNotification?.('Failed to send attachment', 'error');
    }
  };

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-primary-600">{t('Loading...') || 'Loading...'}</div></div>;
  }

  // `conversations` is loaded from the API into state via `setConversations`.

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Messages')} subtitle={t('Stay connected with your students and teachers') || 'Stay connected with your students and teachers'} />
        
        <main className="flex-1 flex overflow-hidden">
          {/* Conversations List */}
          <div className={`${mobileView === 'chat' ? 'hidden' : 'flex'} md:flex flex-col w-full md:w-80 bg-white border-r border-gray-200 overflow-y-auto`}>
            <div className="p-4 border-b border-gray-200">
              <input
                type="text"
                placeholder={t('Search conversations...')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="divide-y divide-gray-200">
              {conversations.map((conv) => (
                <div
                  key={`${conv.type}-${conv.id}`}
                  onClick={async () => {
                    try {
                      let msgs = [];
                      if (conv.type === 'group') {
                        const msgsRes = await api.get(`/messaging/group-chats/${conv.id}/messages/`);
                        msgs = msgsRes.data || [];
                      } else {
                        const msgsRes = await api.get(`/messaging/private-conversations/${conv.id}/messages/`);
                        msgs = msgsRes.data || [];
                      }

                      const selected = {
                        id: conv.id,
                        name: conv.title,
                        isGroup: conv.type === 'group',
                        avatar: conv.type === 'group' ? null : conv.avatar,
                        subtitle: conv.subtitle || '',
                        participantRoles: conv.type === 'private' ? (conv.participant_roles || []) : [],
                        messages: msgs,
                      };
                      setSelectedChat(selected);
                      setMessages(msgs);
                      setMobileView('chat');

                      // After loading messages (which the server marks as read), refresh conversation list and notify sidebar
                      try {
                        const convsResp = await api.get('/messaging/conversations/');
                        const convsData = convsResp.data || convsResp;
                        const convsListRaw = Array.isArray(convsData) ? convsData : convsData.results || [];
                        const convsList = convsListRaw.map(c => ({ ...c, thumbnail: makeAbsolute(c.thumbnail), avatar: makeAbsolute(c.avatar) }));
                        const totalUnread = convsList.reduce((s, c) => s + (c.unread_count || 0), 0);
                        window.dispatchEvent(new CustomEvent('conversations.unread_updated', { detail: { unreadCount: totalUnread } }));
                        // also update local conversations state so UI reflects latest last_message/unread
                        setConversations(convsList);
                      } catch (e) {
                        console.error('Failed to refresh conversations after opening', e);
                      }
                    } catch (err) {
                      console.error('Failed to load conversation messages', err);
                    }
                  }}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition ${selectedChat?.id === conv.id && selectedChat?.name === conv.title ? 'bg-primary-50' : ''}`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${conv.type === 'group' ? 'bg-purple-500' : 'bg-primary-500'}`}>
                      {conv.type === 'group' ? (
                        conv.thumbnail ? (
                          <img src={conv.thumbnail} alt="module" className="w-full h-full object-cover rounded-full" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-2xl">🎓</div>
                        )
                      ) : (
                        (conv.avatar && <img src={conv.avatar} alt="avatar" className="w-full h-full object-cover rounded-full" />) || conv.title.split(' ').map(n=>n[0]).slice(0,2).join('')
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-gray-800 truncate">{conv.title}</h4>
                        <span className="text-xs text-gray-500">{conv.last_message?.created_at ? new Date(conv.last_message.created_at).toLocaleString() : ''}</span>
                      </div>
                      <p className={`text-xs truncate mb-1 ${conv.type === 'private' ? 'font-medium text-primary-600' : 'text-gray-500'}`}>
                        {conv.type === 'private' ? formatParticipantRoles(conv.participant_roles) : conv.subtitle}
                      </p>
                      <p className="text-sm text-gray-600 truncate">
                        {conv.last_message?.content || (conv.last_message?.has_attachment ? `📎 ${t('Attachment')}` : '')}
                      </p>
                    </div>
                    {conv.unread_count > 0 && (
                      <span className="bg-primary-600 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Chat Area */}
          <div className={`${mobileView === 'list' ? 'hidden' : 'flex'} md:flex flex-1 flex-col`}>
            {selectedChat ? (
              <>
                <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setMobileView('list')}
                      className="md:hidden -ml-1 p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg shrink-0"
                      aria-label={t('Back to conversations')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${selectedChat.isGroup ? 'bg-purple-500' : 'bg-primary-500'}`}>
                      {selectedChat.avatar ? (
                        <img src={makeAbsolute(selectedChat.avatar)} alt="avatar" className="w-full h-full object-cover rounded-full" />
                      ) : (
                        selectedChat.isGroup ? <div className="w-full h-full flex items-center justify-center text-2xl">🎓</div> : (
                          (selectedChat.name || '').split(' ').map(n=>n[0]).slice(0,2).join('')
                        )
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{selectedChat.name}</h3>
                      <p className={`text-xs ${selectedChat.isGroup ? 'text-gray-500' : 'font-medium text-primary-600'}`}>
                        {selectedChat.isGroup ? selectedChat.subtitle : formatParticipantRoles(selectedChat.participantRoles)}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
                  <div className="text-center text-gray-500 text-sm mb-4">Today</div>
                  <div className="space-y-4">
                      {messages.map((msg, idx) => {
                        const isOwn = msg.sender && String(msg.sender.id) === String(user.id);
                        const prev = messages[idx - 1];
                        const showAvatar = !isOwn && (!prev || String(prev.sender?.id) !== String(msg.sender?.id));
                        const avatarSrc = makeAbsolute(msg.sender?.profile_picture) || (`https://ui-avatars.com/api/?name=${encodeURIComponent((msg.sender?.first_name || '') + ' ' + (msg.sender?.last_name || ''))}&background=ddd&color=555`);
                        return (
                          <div key={msg.id || idx} className={`mb-3 flex items-end ${isOwn ? 'justify-end' : 'justify-start'}`}>
                            {!isOwn && showAvatar && (
                              <div className="flex-shrink-0 mr-3">
                                <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200">
                                  <img src={avatarSrc} alt={`${msg.sender?.first_name || ''} ${msg.sender?.last_name || ''}`} className="w-full h-full object-cover" />
                                </div>
                              </div>
                            )}

                            <div className={`max-w-[70%] ${isOwn ? 'text-right' : 'text-left'}`}>
                              {!isOwn && (
                                <div className="text-sm text-gray-600 font-medium">{msg.sender?.first_name} {msg.sender?.last_name}</div>
                              )}
                              <div className={`${isOwn ? 'bg-primary-600 text-white' : 'bg-white text-gray-800'} rounded-lg p-3 shadow-sm mt-1`}>
                                {(msg.content || msg.text) && <div>{msg.content || msg.text}</div>}
                                {msg.attachment && (
                                  isImageAttachment(msg.attachment) ? (
                                    <a href={makeAbsolute(msg.attachment)} target="_blank" rel="noopener noreferrer" className="block mt-1">
                                      <img
                                        src={makeAbsolute(msg.attachment)}
                                        alt={t('Image attachment')}
                                        className="max-w-[220px] max-h-[220px] rounded-lg object-cover border border-black/10"
                                      />
                                    </a>
                                  ) : (
                                    <a
                                      href={makeAbsolute(msg.attachment)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`flex items-center gap-2 mt-1 px-3 py-2 rounded-lg border text-sm ${isOwn ? 'bg-white/10 border-white/20 text-white hover:bg-white/20' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'}`}
                                    >
                                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      <span className="truncate max-w-[180px]">{attachmentFileName(msg.attachment) || t('Attachment')}</span>
                                    </a>
                                  )
                                )}
                                {isOwn && (msg.is_read || msg.read) && (
                                  <span className="ml-2 text-xs text-gray-200">✓</span>
                                )}
                              </div>
                              <div className="text-xs mt-1 text-gray-400">{new Date(msg.created_at || msg.time || msg.timestamp || Date.now()).toLocaleString()}</div>
                            </div>

                            {isOwn && (
                              <div className="flex-shrink-0 ml-3">
                                <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200">
                                  <img src={makeAbsolute(user?.profile_picture) || (`https://ui-avatars.com/api/?name=${encodeURIComponent((user?.first_name || '') + ' ' + (user?.last_name || ''))}&background=2b6cb0&color=fff`)} alt="You" className="w-full h-full object-cover" />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                <div className="bg-white border-t border-gray-200 p-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={t('Type a message...')}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <input
                      type="file"
                      ref={attachmentInputRef}
                      onChange={handleAttachmentChange}
                      className="hidden"
                    />
                    <button
                      onClick={() => attachmentInputRef.current?.click()}
                      title={t('Attach a file') || 'Attach a file'}
                      aria-label={t('Attach a file') || 'Attach a file'}
                      className="p-2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                    </button>
                    <button 
                      onClick={handleSendMessage} 
                      disabled={!messageInput.trim()}
                      className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      {t('Send')}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <svg className="w-24 h-24 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p className="text-lg">{t('Select a conversation to start messaging')}</p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default Messages;
